// old implementation
// for new features, we use the one from libs/ui-components/src/Common/Accordion
// keeping this one for the existing references, for time being

import React from 'react';
import { useFormState } from 'react-final-form';
import { isFunction } from 'lodash';
import { Accordion as LUIAccordion, AccordionGroup as LUIAccordionGroup } from '@lendi-ui/accordion';
import { Button } from '@lendi-ui/button';
import getColour from '@lendi-ui/commons/colours';
import type { IconProps } from '@lendi-ui/icon';
import Add from '@lendi-ui/icon/Add';

import { useAnalytics } from '@funnels/providers';
import { run } from '@funnels/utils';

import { AddButton } from '../style';
import { AccordionContentWrapper, AccordionDeleteWrapper } from './index.style';

interface AccordionProps {
  children?: React.ReactNode;
  addButtonText: string;
  shouldDisableAddButton?: boolean;
  shouldHideAddButton?: boolean;
  onAdd: (e: React.SyntheticEvent<Element, Event>) => void;
}

function Accordion(props: AccordionProps) {
  const formState = useFormState();
  const isOpen = (() => {
    if (!formState.valid && formState.submitFailed) {
      return true;
    }

    return;
  })();

  return (
    <>
      <LUIAccordionGroup isOpen={isOpen} variant="empty">
        {props.children}
      </LUIAccordionGroup>
      {!props.shouldHideAddButton && (
        <AddButton
          variant={'empty'}
          before={<Add color={getColour('interaction', 'main')} width="1.5em" height="1.5em" />}
          onClick={props.onAdd}
          isDisabled={props.shouldDisableAddButton}
          isFullWidth
          data-component="add-button"
        >
          {props.addButtonText}
        </AddButton>
      )}
    </>
  );
}

interface AccordionItemProps {
  header: React.ReactNode;
  children: React.ReactNode;
  onDelete?: () => void;
  headerAfter?: React.ReactElement<IconProps>;
  onExpand?: () => void;
  onCollapse?: () => void;
}

const AccordionItem = (props: AccordionItemProps) => {
  const { tracking } = useAnalytics();
  const [isOpen, setIsOpen] = React.useState(true);
  const header = isFunction(props.header) ? props.header(isOpen) : props.header;
  const onAccordionToggle = () => {
    const { onCollapse, onExpand } = props;
    if (isOpen) {
      if (onCollapse) {
        onCollapse();
      }
      tracking?.accordion.collapsed({
        name: header,
        position: header,
      });
    } else {
      if (onExpand) {
        onExpand();
      }
      tracking?.accordion.expanded({
        name: header,
        position: header,
      });
    }
  };

  return (
    <LUIAccordion isOpen={isOpen}>
      <LUIAccordion.Header
        data-component="accordion-header"
        after={props.headerAfter}
        onClick={run(() => setIsOpen(!isOpen), onAccordionToggle)}
      >
        {header}
      </LUIAccordion.Header>
      <LUIAccordion.Content data-component="accordion-content">
        <AccordionContentWrapper>
          {props.children}
          {props.onDelete && (
            <AccordionDeleteWrapper>
              <Button data-component="delete-button" variant="primary" onClick={props.onDelete} isInverse>
                Delete
              </Button>
            </AccordionDeleteWrapper>
          )}
        </AccordionContentWrapper>
      </LUIAccordion.Content>
    </LUIAccordion>
  );
};

Accordion.Item = AccordionItem;

export default Accordion;
